// src/components/auth/recoveryStates.ts
//
// H-2C2 WP-6 — the frontend half of the recovery-state contract.
//
// The CRM backend (OIDC RP) collapses every deterministic auth failure into
// one of nine canonical recovery codes — see server/src/auth/oidc/recovery.
// The frontend NEVER derives a recovery state itself: it receives a canonical
// code (from a failed `/auth/callback` redirect's `?auth_recovery=` parameter,
// or from a `GET /auth/context` failure body) and renders the matching
// surface. This union and copy table are the frontend mirror of that
// backend-authoritative contract.

export type RecoveryState =
  | 'require_reauth'
  | 'access_revoked'
  | 'workspace_closed'
  | 'degraded'
  | 'malformed'
  | 'loop_blocked'
  | 'cookie_blocked'
  | 'callback_txn_expired'
  | 'callback_txn_consumed';

/** Every canonical recovery state — the runtime mirror of the union above. */
export const RP_RECOVERY_STATES: readonly RecoveryState[] = [
  'require_reauth',
  'access_revoked',
  'workspace_closed',
  'degraded',
  'malformed',
  'loop_blocked',
  'cookie_blocked',
  'callback_txn_expired',
  'callback_txn_consumed',
];

/**
 * A FRONTEND-ONLY surface state — the backend never emits it. It is detected
 * client-side when the CRM's `/auth` BFF answers that the OIDC handoff is
 * disabled (`handoff_oidc_crm` OFF) while the frontend flag is ON. Kept
 * separate from `RecoveryState` so the backend recovery contract stays exactly
 * nine states. Surfaced — deliberately — as a configuration/availability
 * problem, NOT as `degraded` (which would wrongly imply a Central outage).
 */
export type FrontendSurfaceState = 'auth_unavailable';

/** Every state `RecoverySurface` can render: the nine canonical + the
 *  frontend-only `auth_unavailable`. */
export type SurfaceState = RecoveryState | FrontendSurfaceState;

/** Type guard — is an arbitrary value a canonical (backend) recovery state? */
export function isRecoveryState(value: unknown): value is RecoveryState {
  return (
    typeof value === 'string' &&
    (RP_RECOVERY_STATES as readonly string[]).includes(value)
  );
}

/**
 * TERMINAL states: re-authenticating the SAME account cannot escape them, so
 * restarting the flow at `/auth/start` just returns the user to the identical
 * surface — a loop. Their only real forward path is a full sign-out (end the
 * session, land on a clean login, sign in with a different account).
 *
 *  - `access_revoked`   — this account has been removed from the workspace.
 *  - `workspace_closed` — the workspace the session resolves to is closed.
 *    (Central login now prefers an OPEN workspace, so a user with any open
 *    workspace never reaches this surface; when it does show, it is terminal.)
 *
 * Every other state is a transient/session issue for which re-authenticating
 * the same account is the correct next step.
 */
export const TERMINAL_NO_REAUTH_STATES: readonly SurfaceState[] = [
  'access_revoked',
  'workspace_closed',
];

/** Is this a terminal state whose only escape is a full sign-out? */
export function isTerminalNoReauthState(state: SurfaceState): boolean {
  return (TERMINAL_NO_REAUTH_STATES as readonly string[]).includes(state);
}

/** User-facing copy for one recovery surface. Deliberately non-technical. */
export interface RecoveryCopy {
  /** Short headline. */
  title: string;
  /** One or two plain sentences — never a stack trace or a raw error code. */
  message: string;
  /** Label for the primary sign-in action (used when the surface offers
   *  re-authentication rather than a bounded retry). */
  signInCta: string;
}

/**
 * The copy table. One entry per surface state (the nine canonical states plus
 * the frontend-only `auth_unavailable`); the `malformed` copy is intentionally
 * non-technical ("could not verify your workspace session") rather than
 * exposing the internal "malformed response" cause.
 */
export const RECOVERY_COPY: Record<SurfaceState, RecoveryCopy> = {
  require_reauth: {
    title: 'Please sign in again',
    message: 'Your session has ended. Sign in again to pick up where you left off.',
    signInCta: 'Sign in',
  },
  access_revoked: {
    title: 'Access removed',
    message:
      'Your access to this workspace has been removed. If you think this is a mistake, contact a workspace administrator.',
    signInCta: 'Sign in with a different account',
  },
  workspace_closed: {
    title: 'Workspace closed',
    message:
      'This workspace is no longer open. Sign out and sign in again to continue — you can use a different account.',
    signInCta: 'Sign in with a different account',
  },
  degraded: {
    title: 'We’re having trouble connecting',
    message:
      'We could not reach the service. This is usually temporary — please try again in a moment.',
    signInCta: 'Sign in',
  },
  malformed: {
    title: 'We could not verify your workspace session',
    message:
      'Something went wrong while confirming your workspace. Please sign in again to continue.',
    signInCta: 'Sign in',
  },
  loop_blocked: {
    title: 'Sign-in stopped',
    message:
      'We stopped a repeated sign-in attempt to protect your account. Wait a moment, then try signing in again.',
    signInCta: 'Try signing in again',
  },
  cookie_blocked: {
    title: 'Cookies are required to sign in',
    message:
      'Your browser is blocking the cookies we need to sign you in. Enable cookies for this site, then try again.',
    signInCta: 'Try signing in again',
  },
  callback_txn_expired: {
    title: 'Sign-in took too long',
    message: 'Your sign-in request expired before it could finish. Please start over.',
    signInCta: 'Start over',
  },
  callback_txn_consumed: {
    title: 'This sign-in link was already used',
    message:
      'For your security, each sign-in link works only once. Please start over.',
    signInCta: 'Start over',
  },
  auth_unavailable: {
    title: 'Sign-in is temporarily unavailable',
    message:
      'The sign-in service isn’t available right now. This is usually brief — please try again in a few moments.',
    signInCta: 'Sign in',
  },
};
